import {
	selectAvailabilityList,
	selectClientForm,
	selectCouponLoading,
	selectLanguaje,
	selectLocalChannelSesion,
	selectPaymentMethod,
	selectSlug,
	selectWorkDay,
} from '@store/selectors';
import { Injectable } from '@angular/core';
import { selectCartTotals, selectIsLoggedIn, selectOrderType, selectStore } from '@store/selectors';
import { Store } from '@ngrx/store';
import { combineLatest, firstValueFrom, map, Observable, take, takeUntil } from 'rxjs';
import { CartService, I18nService, StoreService, AuthModalService } from '@core/services';
import { MenuController } from '@ionic/angular';
import { Router } from '@angular/router';
import { CART_FIND_TOTALS, COUPON_RESET, COUPON_VALID } from '@store/actions';
import { ClientPersonalInformationForm, WorkDay } from '@core/models';
import { Store as StoreModel, CartTotals, Coupon } from '@core/models';
import { ComponentEnum, OrderTypeEnum, PaymentMethodValid } from '@core/enums';
import { selectAppliedCoupon } from '@store/selectors/coupon.selectors';
import { ISummaryComponent, ISummaryPresenter, PathValue } from './interfaces';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { cartStateProcessor } from '@core/domain/cart';

@Injectable({ providedIn: 'root' })
export class SummaryPresenter extends IAbstractPresenter<ISummaryComponent> implements ISummaryPresenter {
	store$: Observable<StoreModel | null>;
	showPromoInput: boolean = false;
	promoCode: string = '';
	currentSlug: string | null = null;
	serviceType$: Observable<OrderTypeEnum | null>;
	workDay$: Observable<WorkDay | null>;
	loading$: Observable<boolean>;
	cartTotals$!: Observable<CartTotals>;
	translations: ILanguageObject;
	appliedCoupon$: Observable<string | null>;
	clientForm$: Observable<ClientPersonalInformationForm | null>;
	paymentMethod$: Observable<PaymentMethodValid | null>;
	appliedCoupon: string | null = null;
	existProductOutStock: string[] = [];
	modalIsOpen: boolean = false;
	isLoggedIn$: Observable<boolean>;
	existDualPricing: boolean = false;
	decimalPrecision: string = '1.2-2';
	overrideCardContainer: boolean = false;
	showUserSummary: boolean = false;

	showSummary: boolean = false;
	showTips: boolean = false;
	isCart: boolean = false;

	storeSlug$: Observable<string | null>;

	constructor(
		private store: Store,
		private _storeService: StoreService,
		private menuCtrl: MenuController,
		private router: Router,
		private i18nService: I18nService,
		private cartService: CartService,
		private authModalService: AuthModalService
	) {
		super();

		this.store$ = this.store.select(selectStore);
		this.serviceType$ = this.store.select(selectOrderType);
		this.cartTotals$ = this.store.select(selectCartTotals);
		this.isLoggedIn$ = this.store.select(selectIsLoggedIn);
		this.loading$ = this.store.select(selectCouponLoading);
		this.appliedCoupon$ = this.store.select(selectAppliedCoupon);
		this.translations = this.i18nService.getTranslations(ComponentEnum.Summary);
		this.workDay$ = this.store.select(selectWorkDay);
		this.clientForm$ = this.store.select(selectClientForm);
		this.paymentMethod$ = this.store.select(selectPaymentMethod);
		this.storeSlug$ =  this.store.select(selectSlug);
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.view.currentSlug = this._storeService.getCurrentSlug();

		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.view.translations = this.i18nService.getTranslations(ComponentEnum.Summary, lang);
			});

		this.workDay$.pipe(takeUntil(this.destroy$)).subscribe(workDay => {
			this.view.existDualPricing = !!workDay?.doblePrecioId;
		});

		this.store
			.select(selectStore)
			.pipe(takeUntil(this.destroy$))
			.subscribe(store => {
				this.view.decimalPrecision = store?.isArgentina == true ? '1.0-2' : '1.2-2';
			});

		this.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
			if (!loading && !this.view.appliedCoupon) {
				this.view.promoCode = '';
				this.view.showPromoInput = false;
			}
		});

		combineLatest([
			this.cartService.getStoreForStore(),
			this.store.select(selectAvailabilityList),
			this.store.select(selectLocalChannelSesion),
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([cartItems, availabilityList, localChannelSession]) => {
				this.view.existProductOutStock = cartStateProcessor.existProductOutStock(
					cartItems,
					availabilityList,
					localChannelSession
				);
			});

		this.appliedCoupon$.pipe(takeUntil(this.destroy$)).subscribe(coupon => {
			this.view.appliedCoupon = coupon;
			if (coupon) {
				this.view.promoCode = coupon;
				this.view.showPromoInput = true;
			}
		});
	}

	togglePromoInput(): void {
		this.view.showPromoInput = !this.view.showPromoInput;
		if (!this.view.showPromoInput) {
			this.view.promoCode = '';
		}
	}

	convertToUpperCase(event: Event): void {
		const target = event.target as HTMLInputElement;
		if (target) {
			this.view.promoCode = target.value.toUpperCase();
		}
	}

	handlePromoAction(): void {
		const currentCoupon = this.view.appliedCoupon;
		if (currentCoupon) {
			this.store.dispatch(COUPON_RESET());
			this.store.dispatch(CART_FIND_TOTALS());
			this.view.promoCode = '';
			this.view.showPromoInput = false;
		} else if (this.view.promoCode && this.view.promoCode.trim() !== '') {
			this.store.dispatch(COUPON_VALID({ coupon: { code: this.view.promoCode } as Coupon }));
		}
	}

	closeCart(): void {
		this.menuCtrl.close('cartMenu');
	}

	async onProcedCheckout(): Promise<void> {
		const isLoggedIn = await firstValueFrom(this.isLoggedIn$.pipe(take(1)));
		const currentSlug = this._storeService.getCurrentSlug();
		const currentAccountSlug = this._storeService.getCurrentAccountSlug();
		if (isLoggedIn) {
			this.closeCart();
			this.router.navigate([currentAccountSlug, currentSlug, 'checkout']);
		} else {
			const { data } = await this.authModalService.openSignInModal({ visitingCheckout: true });

			if (data == 'continueAsGuest') {
				this.menuCtrl.close();
				this.router.navigate([currentAccountSlug, currentSlug, 'checkout']);
			}
		}
	}

	onPromoCodeChange(value: string): void {
		this.view.promoCode = value.toUpperCase();
	}

	attributeClient<T extends keyof ClientPersonalInformationForm>(key: T): Observable<ClientPersonalInformationForm[T] | undefined> {
		return this.clientForm$.pipe(
			take(1),
			map(item => item?.[key])
		);
	}

	attributeCartTotals<T extends keyof CartTotals>(key: T): Observable<PathValue<CartTotals, T> | undefined> {
		return this.cartTotals$.pipe(
			take(1),
			map(item => {
				const keys = key.split('.');
				let current: unknown = item;

				for (const k of keys) {
					if (current == null || typeof current !== 'object') return;
					current = (current as Record<string, unknown>)[k];
				}

				return current as PathValue<CartTotals, T> | undefined;
			})
		);
	}

	paymentMethod(): Observable<string | null> {
		return this.paymentMethod$.pipe(
			take(1)
		);
	}
}
